import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { submitForm } from "@/lib/forms";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const r = await submitForm("newsletter", { email, lead_type: "Subscriber", source_page: "footer" });
      const already = r?.mirror?.status === "already_subscribed";
      if (already) {
        toast.info("You're already on the list.");
      } else {
        toast.success("Subscribed. Thank you.");
      }
      setEmail("");
    } catch (err) {
      toast.error("Subscription failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-slate1 text-white/85 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <img
              src="/brand/renvia-horizontal-white-transparent.png"
              alt="RenVia"
              className="h-10 w-auto"
            />
            <p className="mt-6 max-w-md text-white/70 leading-relaxed">
              RenVia is a trusted U.S. commercialization partner for Oculus Biologics and the
              XcellerEYES lyophilized amniotic membrane product line — advancing the standard of
              ocular surface care.
            </p>

            <form onSubmit={handleSubscribe} className="mt-8 flex max-w-md" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Get clinical updates"
                data-testid="newsletter-email"
                className="flex-1 bg-white/5 border border-white/10 rounded-l-full px-5 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:border-teal"
              />
              <button
                data-testid="newsletter-submit"
                disabled={busy}
                className="bg-teal text-white px-5 py-3 rounded-r-full text-sm font-semibold hover:bg-teal2 transition-colors disabled:opacity-60"
              >
                {busy ? "..." : "Subscribe"}
              </button>
            </form>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs tracking-[0.2em] uppercase text-teal font-semibold mb-4">
              Product
            </div>
            <ul className="space-y-3 text-sm">
              <li><Link to="/xcellereyes" className="hover:text-teal">XcellerEYES</Link></li>
              <li>
                <a
                  href="https://www.oculusbiologics.com/support/order/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-order-link"
                  className="hover:text-teal inline-flex items-center gap-1.5"
                >
                  Order XcellerEYES <span aria-hidden>↗</span>
                </a>
              </li>
              <li><Link to="/resources" className="hover:text-teal">Clinical Resources</Link></li>
              <li><Link to="/contact" className="hover:text-teal">Request a Demo</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs tracking-[0.2em] uppercase text-teal font-semibold mb-4">
              Company
            </div>
            <ul className="space-y-3 text-sm">
              <li><Link to="/about" className="hover:text-teal">About RenVia</Link></li>
              <li><Link to="/careers" className="hover:text-teal">Careers</Link></li>
              <li><Link to="/contact" className="hover:text-teal">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs tracking-[0.2em] uppercase text-teal font-semibold mb-4">
              Contact
            </div>
            <ul className="space-y-3 text-sm text-white/75">
              <li>
                <a
                  href="mailto:info@renviahealth.com"
                  data-testid="footer-email-link"
                  className="hover:text-teal2 transition-colors"
                >
                  info@renviahealth.com
                </a>
              </li>
              <li>1-800-RENVIA-1</li>
              <li>United States</li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs text-white/50">
            © {new Date().getFullYear()} RenVia, Inc. All rights reserved. RenVia is a U.S.
            commercialization partner for Oculus Biologics.
          </p>
          <p className="text-xs text-white/40">
            For licensed eye care professionals. Not medical advice.
          </p>
        </div>
      </div>
    </footer>
  );
}
